import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { LoadingSpinner, EmptyState } from 'components/common';
import ObjectiveMatrix, { BLOCKS } from './ObjectiveMatrix';
import CseComparisonTable from './CseComparisonTable';
import StageCard from './StageCard';
import StatTile from './StatTile';
import ObjectiveLogicNote from './ObjectiveLogicNote';
import { pct, ratioTone, fmtCount, RATIO_TONES, SEGMENT_COLORS } from './objectiveFormat';

const ALL = 'ALL';

/**
 * Obj.2 — "SCHEDULE SUM": how often Engineering met the schedule CSD asked for,
 * and then the schedule Engineering itself committed to. One row of the source
 * spreadsheet = one job (P/J number), not one change order.
 *
 * Reading order is deliberate: two headline rates → the three drawing stages as
 * bars → the CSE ranking → (on demand) the exact figures that reconcile with
 * the year-end spreadsheet. The exact matrix is collapsed by default because it
 * is a reconciliation tool, not the thing anyone opens this tab to see.
 */
const ScheduleSummaryTab = ({ active, objectiveData, objectiveLoading: loading, objectiveError: error, onTotalsChange }) => {
  const data = objectiveData?.schedule_summary;
  const [selectedCse, setSelectedCse] = useState(ALL);
  const [showExact, setShowExact] = useState(false);

  const groups = useMemo(() => data?.groups ?? [], [data]);
  const selectedGroup = useMemo(
    () => groups.find((group) => group.cse === selectedCse) ?? groups[0],
    [groups, selectedCse]
  );

  useEffect(() => {
    if (!active || !data) return;
    onTotalsChange(`${fmtCount(data.totals?.jobs)} jobs · ${fmtCount(data.totals?.change_orders)} change orders`);
  }, [active, data, onTotalsChange]);

  // A CSE can disappear when the filter narrows — fall back to ALL rather than
  // showing an empty matrix for a code that is no longer in the result.
  useEffect(() => {
    if (groups.length > 0 && !groups.some((group) => group.cse === selectedCse)) setSelectedCse(ALL);
  }, [groups, selectedCse]);

  const handleSelect = useCallback((cse) => setSelectedCse(cse), []);

  if (loading && !data) return <LoadingSpinner />;
  if (error) return <EmptyState title="Unable to load schedule summary" description={error} />;
  if (!data || groups.length === 0) {
    return <EmptyState title="No jobs in this range" description="Widen the entry-date range or clear the filters." />;
  }

  const allGroup = groups.find((group) => group.cse === ALL);
  const isAll = selectedGroup?.cse === ALL;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Headline — always the whole filtered scope, never the selected CSE */}
      <div className="tw-flex tw-flex-wrap tw-gap-3">
        <StatTile
          label="Meet / earlier CSD schedule"
          value={pct(allGroup?.headline?.meet_schedule_ratio, 1)}
          hint="Average of the APD, SHOP and ERD rates"
          tone={RATIO_TONES.neutral}
          accent={SEGMENT_COLORS.primary}
          bar={allGroup?.headline?.meet_schedule_ratio ?? 0}
        />
        <StatTile
          label="Meet / earlier committed schedule"
          value={pct(allGroup?.headline?.meet_committed_ratio, 1)}
          hint="Average of the APD, SHOP and ERD rates"
          tone={ratioTone(allGroup?.headline?.meet_committed_ratio)}
          accent={ratioTone(allGroup?.headline?.meet_committed_ratio)}
          bar={allGroup?.headline?.meet_committed_ratio ?? 0}
        />
        <StatTile
          label="Scope"
          value={fmtCount(data.totals?.jobs)}
          hint={`jobs · ${fmtCount(data.totals?.change_orders)} change orders`}
          accent="#cbd5e1"
        />
      </div>

      {/* Scope selector + the three drawing stages for whatever is selected */}
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <span className="tw-mr-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">
            Breakdown for
          </span>
          {groups.map((group) => (
            <button
              key={group.cse}
              type="button"
              onClick={() => handleSelect(group.cse)}
              className={`tw-cursor-pointer tw-rounded-xl tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition-colors ${
                group.cse === selectedCse
                  ? 'tw-border-primary-600 tw-bg-primary-600 tw-text-white'
                  : 'tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600'
              }`}
            >
              {group.cse}
              <span className={`tw-ml-1.5 tw-font-normal ${group.cse === selectedCse ? 'tw-text-white/70' : 'tw-text-slate-400'}`}>
                {fmtCount(group.jobs)}
              </span>
            </button>
          ))}
        </div>

        <div className="tw-flex tw-flex-wrap tw-gap-3">
          {BLOCKS.map((block) => (
            <StageCard
              key={block.key}
              title={block.title}
              subtitle={`${isAll ? 'All CSE' : selectedGroup.cse} · jobs in scope`}
              stats={selectedGroup?.[block.key]}
            />
          ))}
        </div>
      </div>

      {/* Ranking */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
          <h3 className="tw-mb-0 tw-text-[13px] tw-font-semibold tw-text-slate-700">Comparison by CSE order entry</h3>
          <span className="tw-text-[11px] tw-text-slate-400">Click a row to switch the breakdown above · click a header to sort</span>
        </div>
        <CseComparisonTable groups={groups} selectedCse={selectedCse} onSelect={handleSelect} />
      </div>

      {/* Reconciliation view */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <button
          type="button"
          onClick={() => setShowExact((open) => !open)}
          className="tw-inline-flex tw-w-fit tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-semibold tw-text-slate-600 hover:tw-text-primary-600"
        >
          {showExact ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
          Exact figures for {selectedGroup?.cse}
          <span className="tw-font-normal tw-text-slate-400">(matches the year-end spreadsheet)</span>
        </button>
        {showExact && <ObjectiveMatrix group={selectedGroup} />}
      </div>

      <ObjectiveLogicNote items={[
        'The dashboard date range (entry date) and sidebar filters define the jobs and change orders in scope.',
        'Objectives include CO#00 — the job entry itself — so change-order counts here are higher than on the Overview tab, which excludes CO#00.',
        'One job means one P/J number. All buildings and change orders with the same J# are grouped together.',
        'A stage is measurable only after CSD has requested it. A job is Meet only when every requested part or phase meets the comparison date; one late item makes the whole job Delayed, and missing dates remain Pending.',
        'For CSD schedule, Engineering release is compared with the CSD requested date. For committed schedule, Engineering release is compared with the date Engineering committed.',
        'When a job has multiple CSE OE handles, it appears in every matching CSE group. ALL counts it once, so CSE group totals are not additive to ALL.',
        'Headline percentages are the average of the APD, SHOP and ERD rates; each stage rate uses only jobs in scope for that stage.',
      ]} />
    </div>
  );
};

ScheduleSummaryTab.propTypes = {
  active:           PropTypes.bool.isRequired,
  objectiveData:    PropTypes.object,
  objectiveLoading: PropTypes.bool,
  objectiveError:   PropTypes.string,
  onTotalsChange:   PropTypes.func.isRequired,
};

export default ScheduleSummaryTab;
